import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { RouterProvider, createBrowserRouter } from "react-router";

import { setUnauthorizedHandler } from "@/api/client";
import { createQueryClient } from "@/api/query-client";
import { clearSession } from "@/auth/session";
import { i18n } from "@/i18n/i18n";
import { appRoutes } from "@/routes/app-routes";
import { Toaster, toast } from "@punto-firma/ui/components/toaster";
import { TooltipProvider } from "@punto-firma/ui/components/tooltip";

/**
 * Un 401 en cualquier llamada (salvo `/v1/me`, que es como se pregunta si hay
 * sesión) vacía la sesión; las guardas llevan entonces al login.
 */
const useUnauthorizedHandler = (queryClient: QueryClient) => {
  useEffect(() => {
    setUnauthorizedHandler(() => {
      toast.error(i18n.t("auth:sessionExpired"), { id: "session-expired" });
      clearSession(queryClient);
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient]);
};

export const Providers = ({
  queryClient,
  children,
}: {
  queryClient: QueryClient;
  children: React.ReactNode;
}) => {
  useUnauthorizedHandler(queryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {children}
        <Toaster position="bottom-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export const App = () => {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(() => createBrowserRouter(appRoutes));

  return (
    <Providers queryClient={queryClient}>
      <RouterProvider router={router} />
    </Providers>
  );
};
